import { useState } from 'react';

import {
  HStack,
  Heading,
  NewButton,
  Paragraph,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';

interface CustomChatInstructionsProps {
  initialValue: string;
  onSave: (value: string) => Promise<void>;
}

export const CustomChatInstructions = ({
  initialValue,
  onSave,
}: CustomChatInstructionsProps) => {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = value !== initialValue;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(value);
      setIsEditing(false);
    } catch {
      toast.error('Failed to save custom instructions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  return (
    <div className="w-full rounded-lg border border-border-primary bg-bg-secondary p-6">
      <VStack gap={4} className="w-full">
        <HStack className="w-full items-start justify-between">
          <VStack gap={1}>
            <Heading size="md" weight="semibold">
              Custom Chat Instructions
            </Heading>
            <Paragraph size="sm" color="secondary">
              Customize your chat experience by providing specific guidelines
            </Paragraph>
          </VStack>
          {!isEditing && (
            <NewButton
              text="Edit"
              type="outline"
              size="s"
              icon={{ name: 'Pencil', position: 'left' }}
              onClick={() => setIsEditing(true)}
            />
          )}
        </HStack>

        {isEditing ? (
          <VStack gap={3} className="w-full">
            <Textarea
              placeholder="Type your instructions here"
              rows={6}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <HStack gap={2} className="justify-end">
              <NewButton
                text="Cancel"
                type="outline"
                size="s"
                onClick={handleCancel}
              />
              <NewButton
                text="Save"
                type="primary"
                size="s"
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                loading={isSaving}
              />
            </HStack>
          </VStack>
        ) : (
          <div className="w-full">
            {value ? (
              <Paragraph
                size="sm"
                color="secondary"
                className="whitespace-pre-wrap"
              >
                {value}
              </Paragraph>
            ) : (
              <Paragraph size="sm" color="tertiary">
                No custom instructions set
              </Paragraph>
            )}
          </div>
        )}
      </VStack>
    </div>
  );
};
